import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import useAuth from "../../hooks/useAuth";
import { isRepoUser } from "../../utils/permissions";
import { notificationService } from "../../services/notification.service";
import "../../styles/notificationPanel.css";

const PAGE_SIZE = 15;

function formatWhen(value) {
  if (!value) return "";
  try {
    return new Date(value).toLocaleString([], {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

function mergeItems(current, incoming) {
  const seen = new Set(current.map((item) => String(item._id)));
  const next = [...current];
  incoming.forEach((item) => {
    const id = String(item._id);
    if (seen.has(id)) return;
    seen.add(id);
    next.push(item);
  });
  return next;
}

export default function NotificationPanel() {
  const { auth } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);
  const [error, setError] = useState("");
  const panelRef = useRef(null);
  const markingRef = useRef(false);
  const canOpenConfirmations = isRepoUser(auth?.user?.role);

  const loadNotifications = useCallback(async () => {
    if (!auth?.token || markingRef.current) return;
    try {
      setLoading(true);
      const res = await notificationService.getNotifications(auth.token, {
        limit: PAGE_SIZE,
        page: 1,
        unread: 1,
      });
      const nextItems = Array.isArray(res?.data?.items) ? res.data.items : [];
      const total = Number(res?.data?.unreadCount ?? res?.data?.total ?? nextItems.length);
      setItems(nextItems);
      setUnreadCount(total);
      setPage(1);
      setHasMore(nextItems.length < total);
      setError("");
    } catch {
      setError("Could not load notifications.");
    } finally {
      setLoading(false);
    }
  }, [auth?.token]);

  const loadMore = useCallback(async () => {
    if (!auth?.token || loadingMore || !hasMore) return;
    const nextPage = page + 1;
    try {
      setLoadingMore(true);
      const res = await notificationService.getNotifications(auth.token, {
        limit: PAGE_SIZE,
        page: nextPage,
        unread: 1,
      });
      const incoming = Array.isArray(res?.data?.items) ? res.data.items : [];
      const total = Number(res?.data?.unreadCount ?? 0);
      setItems((current) => mergeItems(current, incoming));
      setHasMore(incoming.length === PAGE_SIZE);
      setUnreadCount(total);
      setPage(nextPage);
    } catch {
      setError("Could not load more notifications.");
    } finally {
      setLoadingMore(false);
    }
  }, [auth?.token, hasMore, loadingMore, page]);

  useEffect(() => {
    loadNotifications();
    const interval = setInterval(loadNotifications, 30000);
    const onChanged = () => loadNotifications();
    window.addEventListener("app:notifications-changed", onChanged);
    return () => {
      clearInterval(interval);
      window.removeEventListener("app:notifications-changed", onChanged);
    };
  }, [loadNotifications]);

  useEffect(() => {
    if (!open) return undefined;

    const onDocClick = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const handleToggle = () => {
    setOpen((prev) => !prev);
    if (!open) loadNotifications();
  };

  const handleScroll = (event) => {
    const el = event.currentTarget;
    if (el.scrollTop + el.clientHeight < el.scrollHeight - 32) return;
    loadMore();
  };

  const handleMarkRead = async (item) => {
    if (!item?._id || !auth?.token) return;
    setItems((prev) => prev.filter((row) => row._id !== item._id));
    setUnreadCount((prev) => Math.max(0, prev - 1));
    try {
      await notificationService.markRead(item._id, auth.token);
      setError("");
    } catch {
      setError("Could not update this notification.");
      loadNotifications();
    }
  };

  const handleNotificationClick = async (item) => {
    await handleMarkRead(item);

    const confirmationId = item?.meta?.confirmationId;
    if (item?.type === "VEHICLE_TRACED") {
      setOpen(false);
      navigate("/confirmation?status=PENDING");
      return;
    }
    if (!confirmationId) return;

    const isRepoAdmin = auth?.user?.role === "REPO_ADMIN";

    if (item?.type === "INVENTORY_SUBMITTED" && isRepoAdmin) {
      setOpen(false);
      navigate(`/confirmation/${confirmationId}`);
      return;
    }

    const isTracerInventory =
      item?.type === "TRACE_CONFIRMED" ||
      item?.type === "INVENTORY_REVISION_REQUESTED" ||
      item?.type === "INVENTORY_CONFIRMED" ||
      item?.meta?.action === "INVENTORY_UPDATE";

    if (isTracerInventory) {
      setOpen(false);
      navigate(`/inventory-update?confirmationId=${confirmationId}`);
    }
  };

  const handleMarkAllRead = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!auth?.token || markingAll || unreadCount === 0) return;
    const previousItems = items;
    const previousCount = unreadCount;
    setMarkingAll(true);
    markingRef.current = true;
    setItems([]);
    setUnreadCount(0);
    setHasMore(false);
    setError("");
    try {
      await notificationService.markAllRead(auth.token);
    } catch {
      setItems(previousItems);
      setUnreadCount(previousCount);
      setError("Could not mark all as read.");
    } finally {
      markingRef.current = false;
      setMarkingAll(false);
    }
  };

  return (
    <div className="notification-panel" ref={panelRef}>
      <button
        type="button"
        className="notification-bell"
        onClick={handleToggle}
        aria-label="Notifications"
        aria-expanded={open}
      >
        <span className="notification-bell__icon" aria-hidden>🔔</span>
        {unreadCount > 0 && (
          <span className="notification-badge">{unreadCount > 99 ? "99+" : unreadCount}</span>
        )}
      </button>

      {open && (
        <div className="notification-dropdown">
          <div className="notification-dropdown__head">
            <div>
              <strong>Notifications</strong>
              <span className="notification-dropdown__count">
                {unreadCount > 0 ? `${unreadCount} new` : "You're caught up"}
              </span>
            </div>
            <button
              type="button"
              className="notification-mark-all"
              onClick={handleMarkAllRead}
              disabled={markingAll || unreadCount === 0}
            >
              {markingAll ? "Marking…" : "Mark all read"}
            </button>
          </div>

          <div
            className="notification-dropdown__body"
            onScroll={handleScroll}
          >
            {error ? <p className="notification-error">{error}</p> : null}
            {loading && items.length === 0 ? (
              <p className="notification-empty">Loading notifications…</p>
            ) : items.length === 0 ? (
              <p className="notification-empty">No new notifications.</p>
            ) : (
              items.map((item) => {
                const vehicleNumber = String(item?.meta?.vehicleNumber || "").trim();
                return (
                  <button
                    key={item._id}
                    type="button"
                    className="notification-item notification-item--unread"
                    onClick={() => handleNotificationClick(item)}
                  >
                    <span className="notification-item__top">
                      <span className="notification-item__title">{item.title}</span>
                      <span className="notification-item__time">{formatWhen(item.createdAt)}</span>
                    </span>
                    <span className="notification-item__message">{item.message}</span>
                    {vehicleNumber ? (
                      <span className="notification-item__vehicle">{vehicleNumber}</span>
                    ) : null}
                  </button>
                );
              })
            )}
            {loadingMore ? <p className="notification-empty">Loading more…</p> : null}
          </div>
          {canOpenConfirmations ? (
            <div className="notification-dropdown__foot">
              <button
                type="button"
                className="notification-pending-link"
                onClick={() => {
                  setOpen(false);
                  navigate("/confirmation?status=PENDING");
                }}
              >
                Pending confirmations
              </button>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
